import React, { useCallback, useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  ReferenceLine,
} from "recharts";
import toast from "react-hot-toast";
import FeaturedIcon from "../assets/images/beattle.png";
import ConnectIcon from "../assets/images/connect.png";
import PersonIcon from "../assets/images/person.png";
import NewUser from "../assets/images/newUser.png";
import ModifiedUser from "../assets/images/UserMod.png";
import resolvedIssue from "../assets/images/resolve.png";
import editMerc from "../assets/images/editMerc.png";
import MercProfile from "../assets/images/MercProfile.png";
import Kate from "../assets/images/kate.png";
import Craig from "../assets/images/craig.png";
import Andi from "../assets/images/andi.png";
import Cano from "../assets/images/cano.png";
import Diggs from "../assets/images/diggs.png";
import {
  getDashboardInfo,
  getTransactions,
  getGraphData,
} from "../services/transactionService";
import { getCurrentDate, getDateAMonthAgo } from "../utils";
import ProfileNav from "../components/ProfileNav";
import { getTenantFromUrl } from "../helpers/http";

const Dashboard = () => {
  const [startDate, setStartDate] = useState(getDateAMonthAgo());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [startingDate, setStartingDate] = useState(
    new Date(new Date().getTime() - 1 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0]
  );
  const [endingDate, setEndingDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [scheme, setScheme] = useState(null);
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [graphInfo, setGraphInfo] = useState([]);

  const getDashboardStats = useCallback(async () => {
    try {
      const tenant = getTenantFromUrl();
      const res = await getDashboardInfo({
        startDate,
        endDate,
        scheme,
        status,
      }, tenant);
      setStats(res.data);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  }, [startDate, endDate, scheme, status]);

  const fetchTransactions = useCallback(async () => {
    try {
      const tenant = getTenantFromUrl();
      const res = await getTransactions(1, 5, {
        startDate: startingDate,
        endDate: endingDate,
        scheme,
        status,
      }, tenant);
      setTransactions(res.data?.result);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  }, [startingDate, endingDate, scheme, status]);

  const GraphData = useCallback(async () => {
    const tenant = getTenantFromUrl();
    const graph = await getGraphData(null, tenant);

    // Ensure data is sorted in ascending order by date
    const sortedGraphData = graph.data.sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    setGraphInfo(sortedGraphData);

    // setGraphInfo(graph.data);
  }, []);

  useEffect(() => {
    getDashboardStats();
    fetchTransactions();
    GraphData();
  }, [getDashboardStats, fetchTransactions, GraphData]);

  const datas = [
    { date: "Nov 08", amount: 200000 },
    { date: "Nov 09", amount: 250000 },
  ];

  const formatYAxis = (tickItem) => {
    return tickItem >= 1000000
      ? `${(tickItem / 1000000).toFixed(1)}M`
      : tickItem;
  };

  const getCssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();

  const primaryColor = getCssVar("--primary") || "#F58220";

  return (
    <div>
      <div className=" pt-5 ">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-navy-blue">Dashboard</h1>

          <div>
            <ProfileNav />
          </div>
        </div>
        <div className="mt-2 w-full flex">
          <div className="w-4/5">
            <div className="flex items-center justify-end">
              {/* <h1 className="text-2xl font-bold text-navy-blue">Dashboard</h1> */}
              <div className="flex items-center">
                <div className="bg-white text-gray-400 font-semibold h-[40px] flex items-center gap-2 px-5 w-[190px]">
                  <p>25-07-23 10:05am</p>
                  <span>
                    <svg
                      className="mt-1"
                      width="10"
                      height="5"
                      viewBox="0 0 10 5"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        opacity="0.5"
                        d="M1 0.5L5 4.5L9 0.5"
                        stroke="black"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex items-center gap-5">
                <section className="w-[207.75px] h-[115.67px] flex flex-col gap-3 p-4 rounded-lg bg-[rgba(var(--secondary-rgb),0.5)]">
                  <p>Approved Transactions</p>
                  <span className="flex justify-between">
                    <p className="text-2xl text font-semibold">
                      {stats.approvedTransactionCount}
                    </p>
                  </span>
                </section>
                <section className="w-[207.75px] h-[115.67px] flex flex-col gap-3 p-4 rounded-lg bg-[rgba(var(--primary-rgb),0.5)]">
                  <p>System Users</p>
                  <span className="flex justify-between">
                    <p className="text-2xl text font-semibold">
                      {stats.userCount}
                    </p>
                  </span>
                </section>
                <section className="w-[207.75px] h-[115.67px] flex flex-col gap-3 p-4 rounded-lg bg-[rgba(var(--secondary-rgb),0.5)]">
                  <p>Declined Transactions</p>
                  <span className="flex justify-between">
                    <p className="text-2xl text font-semibold">
                      {stats.declinedTransactionCount}
                    </p>
                  </span>
                </section>
                <section className="w-[207.75px] h-[115.67px] flex flex-col gap-3 p-4 rounded-lg bg-[rgba(var(--primary-rgb),0.5)]">
                  <p>Merchants</p>
                  <span className="flex justify-between">
                    <p className="text-2xl text font-semibold">
                      {stats.merchantCount}
                    </p>
                  </span>
                </section>
              </div>
            </div>
            <div className="bg-white  h-[326px] mt-4 p-5 rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="border-r pr-4 text-sm font-bold text-black-shade">
                    Transaction Analytics
                  </p>
                  <div className="flex items-center border-r px-4">
                    <div className="flex items-center gap-1 mr-2">
                      <div className="rounded-full bg-green-400 w-[6px] h-[6px]"></div>
                      <p className="text-[12px] font-normal text-black-shade">
                        Approved
                      </p>
                    </div>
                    <div className="flex items-center gap-1 mr-2">
                      <div className="rounded-full bg-red-500 w-[6px] h-[6px]"></div>
                      <p className="text-[12px] font-normal text-black-shade">
                        Declined
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="rounded-full bg-text-orange w-[6px] h-[6px]"></div>
                      <p className="text-[12px] font-normal text-black-shade">
                        Initiated
                      </p>
                    </div>
                  </div>
                  {/* <div className="flex items-center px-2 gap-2">
                    <p className="text-[12px] font-normal text-black-shade">
                      Transfers
                    </p>
                    <span>
                      <svg
                        width="12"
                        height="9"
                        viewBox="0 0 12 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#clip0_27_8380)">
                          <path
                            d="M3 4.06372L5.5 6.06372L8 4.06372"
                            stroke="#1C1C1C"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_27_8380">
                            <rect
                              width="8.25"
                              height="12"
                              fill="white"
                              transform="translate(12 0.563721) rotate(90)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                  </div> */}
                </div>
                <span className="flex justify-center items-center gap-2 ">
                  <div className="text-sm py-[0.1rem] px-1 font-medium flex justify-center items-center gap-2 border border-orange-300 ">
                    {new Date().toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                    <span>
                      <svg
                        width="15"
                        height="14"
                        viewBox="0 0 15 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M1.85303 3.49998C1.85303 2.85565 2.37536 2.33331 3.01969 2.33331H11.1864C11.8307 2.33331 12.353 2.85565 12.353 3.49998V11.6666C12.353 12.311 11.8307 12.8333 11.1864 12.8333H3.01969C2.37536 12.8333 1.85303 12.311 1.85303 11.6666V3.49998Z"
                          stroke="#F58220"
                          stroke-width="1.16667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M9.43628 1.16669V3.50002"
                          stroke="#F58220"
                          stroke-width="1.16667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M4.76978 1.16669V3.50002"
                          stroke="#F58220"
                          stroke-width="1.16667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M1.85303 5.83331H12.353"
                          stroke="#F58220"
                          stroke-width="1.16667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                  <div className="border border-orange-300 px-[0.2rem] py-[]">
                    <svg
                      width="15"
                      height="14"
                      viewBox="0 0 15 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.10295 8.16665C7.74728 8.16665 8.26961 7.64431 8.26961 6.99998C8.26961 6.35565 7.74728 5.83331 7.10295 5.83331C6.45861 5.83331 5.93628 6.35565 5.93628 6.99998C5.93628 7.64431 6.45861 8.16665 7.10295 8.16665Z"
                        stroke="#F58220"
                        strokeWidth="1.16667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M11.7697 8.16665C12.414 8.16665 12.9364 7.64431 12.9364 6.99998C12.9364 6.35565 12.414 5.83331 11.7697 5.83331C11.1254 5.83331 10.603 6.35565 10.603 6.99998C10.603 7.64431 11.1254 8.16665 11.7697 8.16665Z"
                        stroke="#F58220"
                        strokeWidth="1.16667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2.43644 8.16665C3.08077 8.16665 3.60311 7.64431 3.60311 6.99998C3.60311 6.35565 3.08077 5.83331 2.43644 5.83331C1.79211 5.83331 1.26978 6.35565 1.26978 6.99998C1.26978 7.64431 1.79211 8.16665 2.43644 8.16665Z"
                        stroke="#F58220"
                        strokeWidth="1.16667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </span>
              </div>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  width={800}
                  height={400}
                  data={graphInfo}
                  margin={{
                    top: 10,
                    right: 30,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis tickFormatter={formatYAxis} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    stroke={primaryColor}
                    fill={primaryColor}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 bg-white px-12 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <p className="border-r pr-4 text-sm font-bold text-black-shade">
                    Recent Transactions
                  </p>
                  <div className="flex items-center border-r px-4">
                    <div className="flex items-center gap-1 mr-2">
                      <div className="rounded-full bg-green-400 w-[6px] h-[6px]"></div>
                      <p className="text-[12px] font-normal text-black-shade">
                        Approved
                      </p>
                    </div>
                    <div className="flex items-center gap-1 mr-2">
                      <div className="rounded-full bg-red-500 w-[6px] h-[6px]"></div>
                      <p className="text-[12px] font-normal text-black-shade">
                        Declined
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="rounded-full bg-text-orange w-[6px] h-[6px]"></div>
                      <p className="text-[12px] font-normal text-black-shade">
                        Initiated
                      </p>
                    </div>
                  </div>
                  {/* <div className="flex items-center border-r px-2 gap-2">
                    <p className="text-[12px] font-normal text-black-shade">
                      Merchant
                    </p>
                    <span>
                      <svg
                        width="12"
                        height="9"
                        viewBox="0 0 12 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#clip0_27_8380)">
                          <path
                            d="M3 4.06372L5.5 6.06372L8 4.06372"
                            stroke="#1C1C1C"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_27_8380">
                            <rect
                              width="8.25"
                              height="12"
                              fill="white"
                              transform="translate(12 0.563721) rotate(90)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                  </div> */}
                  {/* <div className="flex items-center px-2 gap-2">
                    <p className="text-[12px] font-normal text-black-shade">
                      Transaction Type: Cards
                    </p>
                    <span>
                      <svg
                        width="12"
                        height="9"
                        viewBox="0 0 12 9"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#clip0_27_8380)">
                          <path
                            d="M3 4.06372L5.5 6.06372L8 4.06372"
                            stroke="#1C1C1C"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_27_8380">
                            <rect
                              width="8.25"
                              height="12"
                              fill="white"
                              transform="translate(12 0.563721) rotate(90)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                  </div> */}
                </div>
                <span className="flex justify-center items-center gap-2 ">
                  <div className="text-sm py-[0.1rem] px-1 font-medium flex justify-center items-center gap-2 border border-orange-300 ">
                    {new Date().toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                    <span>
                      <svg
                        width="15"
                        height="14"
                        viewBox="0 0 15 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M1.85303 3.49998C1.85303 2.85565 2.37536 2.33331 3.01969 2.33331H11.1864C11.8307 2.33331 12.353 2.85565 12.353 3.49998V11.6666C12.353 12.311 11.8307 12.8333 11.1864 12.8333H3.01969C2.37536 12.8333 1.85303 12.311 1.85303 11.6666V3.49998Z"
                          stroke="#F58220"
                          stroke-width="1.16667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M9.43628 1.16669V3.50002"
                          stroke="#F58220"
                          stroke-width="1.16667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M4.76978 1.16669V3.50002"
                          stroke="#F58220"
                          stroke-width="1.16667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M1.85303 5.83331H12.353"
                          stroke="#F58220"
                          stroke-width="1.16667"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                  <div className="border border-orange-300 px-[0.2rem] py-[0.58rem]">
                    <svg
                      width="15"
                      height="14"
                      viewBox="0 0 15 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7.10295 8.16665C7.74728 8.16665 8.26961 7.64431 8.26961 6.99998C8.26961 6.35565 7.74728 5.83331 7.10295 5.83331C6.45861 5.83331 5.93628 6.35565 5.93628 6.99998C5.93628 7.64431 6.45861 8.16665 7.10295 8.16665Z"
                        stroke="#F58220"
                        strokeWidth="1.16667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M11.7697 8.16665C12.414 8.16665 12.9364 7.64431 12.9364 6.99998C12.9364 6.35565 12.414 5.83331 11.7697 5.83331C11.1254 5.83331 10.603 6.35565 10.603 6.99998C10.603 7.64431 11.1254 8.16665 11.7697 8.16665Z"
                        stroke="#F58220"
                        strokeWidth="1.16667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2.43644 8.16665C3.08077 8.16665 3.60311 7.64431 3.60311 6.99998C3.60311 6.35565 3.08077 5.83331 2.43644 5.83331C1.79211 5.83331 1.26978 6.35565 1.26978 6.99998C1.26978 7.64431 1.79211 8.16665 2.43644 8.16665Z"
                        stroke="#F58220"
                        strokeWidth="1.16667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </span>{" "}
              </div>
              <div className="overflow-x-auto mt-6">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                  <thead className="bg-gray-100 text-gray-600 text-sm">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">
                        Transaction ID
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Masked PAN
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left font-semibold">
                        Customer Name
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Scheme
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-center font-semibold">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {transactions.map((transaction, index) => (
                      <tr key={index} className="hover:bg-orange-50 transition">
                        <td className="px-4 py-3">{transaction.id}</td>
                        <td className="px-4 py-3">{transaction.pan}</td>
                        <td
                          className="px-4 py-3 max-w-[200px] truncate"
                          title={transaction.description}
                        >
                          {transaction.description}
                        </td>
                        <td className="px-4 py-3">
                          {transaction.customerName}
                        </td>
                        <td className="px-4 py-3 text-center font-medium text-gray-700">
                          {transaction.scheme}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold">
                          {transaction.amount}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {transaction.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="w-[20%] h-full  border-l-2 border-gray-300">
            <div className="">
              <div className="bg-white w-[full] px-4 py-2 rounded-md">
                <div className="flex items-center justify-between">
                  <span>
                    <p>System</p>
                    <p>Notifications</p>
                  </span>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={FeaturedIcon} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      A merchant registration was...
                      {/* /*declined downtime*/}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      Just now
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={PersonIcon} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      New merchant registered{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      59 minutes ago
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={PersonIcon} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      New merchant registered.{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      59 minutes ago
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={FeaturedIcon} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      You have a bug that needs to be...{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      12 hours ago
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={ConnectIcon} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Andi Lane subscribed to you.{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      Today, 11.59AM now
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white w-[full] px-4 py-2 rounded-md">
                <div className="flex items-center justify-between">
                  <span>
                    <p>Admin System</p>
                    <p>Activities</p>
                  </span>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={NewUser} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      New User was created {/* /*declined downtime*/}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      Just now
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={editMerc} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Edited a merchant profile{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      59 minutes ago
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={resolvedIssue} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Resolved a reported issue{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      12 hours ago
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={ModifiedUser} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Modified a user’s data{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      Today, 11.59AM now
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={MercProfile} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Disabled a merchant profile{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      Feb 2, 2023{" "}
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={MercProfile} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Enable a merchant profile{" "}
                    </p>
                    <p className="text-[12px] font-norma text-gray-400">
                      Feb 2, 2023{" "}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white w-[full] px-4 py-2 rounded-md">
                <div className="flex items-center justify-between">
                  <span>
                    <p>Recently Created</p>
                    <p>Users</p>
                  </span>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={Craig} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Natali Craig{" "}
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={Cano} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Drew Cano{" "}
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={Diggs} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Orlando Diggs{" "}
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={Andi} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Andi Lane{" "}
                    </p>
                  </div>
                </div>
                <div className="flex mt-3 gap-2">
                  <img src={Kate} alt="icon" className="w-6 h-6" />
                  <div>
                    <p className="text-[13px] font-normal text-black-shade">
                      Kate Morrison{" "}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>
        {`
.switch {
  position: relative;
  display: inline-block;
  width: 33.12px;
  height: 17px;
}

.switch input { 
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #EBEBEB;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 17px;
  width: 17px;
  left: 2px;
  
  background-color: #CDCDCD;
  -webkit-transition: .4s;
  transition: .4s;
}

input:checked + .slider {
  background-color: #2196F3;
}

input:focus + .slider {
  box-shadow: 0 0 1px #2196F3;
}

input:checked + .slider:before {
  -webkit-transform: translateX(18px);
  -ms-transform: translateX(18px);
  transform: translateX(18px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.table-row {
  padding: 10px; /* Adjust the padding value as needed */
}`}
      </style>
    </div>
  );
};

export default Dashboard;
